import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'motion/react';
import './TypingAnimation.css';

interface TypingAnimationProps {
  text: string;
  speed?: number; // milliseconds per character (default 50ms)
  onComplete?: () => void;
  showCursor?: boolean;
}

/**
 * Character-by-character typing animation component.
 * Used in tutorial step 4 to simulate typing the predefined phrase.
 */
const TypingAnimation: React.FC<TypingAnimationProps> = ({
  text,
  speed = 50,
  onComplete,
  showCursor = true
}) => {
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  // Store latest callback in ref to avoid infinite loops
  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    if (currentIndex < text.length) {
      const timer = setTimeout(() => {
        setDisplayedText(prev => prev + text[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, speed);

      return () => clearTimeout(timer);
    } else if (currentIndex === text.length && !isComplete) {
      // Animation complete
      setIsComplete(true);
      if (onCompleteRef.current) {
        onCompleteRef.current();
      }
    }
  }, [currentIndex, text, speed, isComplete]);

  // Reset when text changes
  useEffect(() => {
    setDisplayedText('');
    setCurrentIndex(0);
    setIsComplete(false);
  }, [text]);

  return (
    <motion.div
      className="typing-animation"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <span className="typing-text" aria-live="polite">
        {displayedText}
      </span>
      {showCursor && !isComplete && (
        <motion.span
          className="typing-cursor"
          animate={{ opacity: [1, 0] }}
          transition={{
            duration: 0.5,
            repeat: Infinity,
            repeatType: 'reverse'
          }}
        >
          |
        </motion.span>
      )}
    </motion.div>
  );
};

export default TypingAnimation;